
<!-- src/views/RotaView.vue -->
<template>
  <div class="section coredeck-page-section">
    <RotaToolbar
      :week-range-text="weekRangeText"
      :can-go-next="canGoNext"
      :can-edit="canAddOrEdit"
      :busy="loading"
      @prev="prevWeek"
      @next="nextWeek"
      @refresh="reload"
    />
    <div v-if="error" class="card-panel red lighten-5 red-text text-darken-2">
      {{ error }}
    </div>
    <div class="card">
      <div class="card-content">
        <RotaGrid
          :staff-rows="staff"
          :week-days="weekDays"
          :can-edit="canAddOrEdit"
          :published="published"
          :edit-enabled="editEnabled"
          :publishing="publishing"
          :publish-error="publishError"
          :can-publish="canPublish"
          :pdf-busy="generatingRotaPdf"
          :is-operations="isOperations"
          :show-copy-previous-week="true"
          :can-copy-previous-week="canCopyPreviousWeek"
          :copy-previous-week-busy="copyPreviousWeekBusy"
          :shifts-for-cell="shiftsForCell"
          :staff-hours-for-week="staffHoursForWeek"
          :day-shift-count="dayShiftCount"
          :day-hours-total="dayHoursTotal"
          :format-time-label="formatTimeLabel"
          :holiday-block-for-cell="holidayBlockForCell"
          @cell-click="onCellClick"
          @shift-click="onShiftClick"
          @holiday-click="onHolidayClick"
          @publish="onPublish"
          @enable-edit="onEnableEdit"
          @generate-pdf="onGenerateRotaPdf"
          @copy-previous-week="onCopyPreviousWeekRota"
        />
      </div>
    </div>
    <!-- Global GM rota table -->
    <GlobalGMrotaTable />
    <ShiftEditorModal
      ref="shiftModal"
      :value="activeShift"
      :busy="saving"
      :staff-options="staff"
      @save="onSaveShift"
      @delete="onDeleteShift"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { functions } from "@/firebase";
import { useVenueContext } from "@/scripts/useScripts/useVenueContext";
import useRota from "@/scripts/useScripts/useRota";
import RotaToolbar from "@/components/rota/RotaToolbar.vue";
import RotaGrid from "@/components/rota/RotaGrid.vue";
import ShiftEditorModal from "@/components/rota/ShiftEditorModal.vue";
import GlobalGMrotaTable from "@/components/rota/GlobalGMrotaTable.vue";
import { fetchHolidayBlocksForDateRange } from "@/scripts/holidayRequestsApi";
import {
  buildAllergenTrainingStatus,
  ensureAllergenTrainingRecordForStaff,
  loadAllergenTrainingRecordsForVenue,
  normaliseStaffId,
} from "@/scripts/training/allergenTrainingGate";

/* global M */

const db = getFirestore();
const auth = getAuth();
const { venueId } = useVenueContext();
const isOperations = ref(false);
let unsubscribeAuth = null;

function userProfileIsOperations(data = {}) {
  return (
    data?.isOperations === true ||
    String(data?.user_type || "")
      .trim()
      .toLowerCase() === "operations"
  );
}

async function resolveOperationsOnlyAccess(user) {
  if (!user?.uid) {
    isOperations.value = false;
    return;
  }
  try {
    const snap = await getDoc(doc(db, "users", user.uid));
    const data = snap.exists() ? snap.data() || {} : {};
    isOperations.value = userProfileIsOperations(data);
  } catch (e) {
    console.warn("[RotaView] failed to resolve operations profile:", e);
    isOperations.value = false;
  }
}

unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
  await resolveOperationsOnlyAccess(user);
});

const {
  loading,
  error,
  staff,
  shifts,
  weekId,
  weekDays,
  weekRangeText,
  canGoNext,
  canAddOrEdit,
  prevWeek,
  nextWeek,
  shiftsForCell,
  staffHoursForWeek,
  dayShiftCount,
  dayHoursTotal,
  formatTimeLabel,
  loadWeek,
  createOrUpdateShift,
  deleteShift,
  makeDefaultShiftDraft,
} = useRota();

/* ─── Allergen training rota gate ───────────────────── */

const allergenTrainingByStaffId = ref({});
const allergenTrainingLoaded = ref(false);

async function loadAllergenTrainingGateForRota(v, staffList) {
  const cleanVenueId = String(v || "").trim();
  allergenTrainingLoaded.value = false;
  if (!cleanVenueId) {
    allergenTrainingByStaffId.value = {};
    allergenTrainingLoaded.value = true;
    return;
  }
  try {
    const existingRecords = await loadAllergenTrainingRecordsForVenue(cleanVenueId);
    const nextMap = {
      ...existingRecords,
    };
    for (const staffRow of staffList || []) {
      const staffId = normaliseStaffId(staffRow);
      if (!staffId) continue;
      if (!nextMap[staffId]) {
        const createdRecord = await ensureAllergenTrainingRecordForStaff({
          venueId: cleanVenueId,
          staff: staffRow,
        });
        nextMap[staffId] = createdRecord;
      }
    }
    allergenTrainingByStaffId.value = nextMap;
  } catch (e) {
    console.error("[RotaView] failed to load allergen training gate:", e);
    allergenTrainingByStaffId.value = {};
    M.toast({
      html:
        e?.message ||
        "Could not load allergen training records. Rota training restrictions may not be available.",
    });
  } finally {
    allergenTrainingLoaded.value = true;
  }
}

function getStaffAllergenTrainingStatus(staffRow) {
  const staffId = normaliseStaffId(staffRow);
  const record = allergenTrainingByStaffId.value[staffId];
  return buildAllergenTrainingStatus(record);
}

function canAddStaffToRota(staffRow) {
  const status = getStaffAllergenTrainingStatus(staffRow);
  return !status.rotaBlocked;
}

function allergenBlockMessage(staffRow) {
  const staffLabel =
    staffRow?.staff_name || staffRow?.staffName || staffRow?.name || "This staff member";
  const status = getStaffAllergenTrainingStatus(staffRow);
  return (
    status.message ||
    `${staffLabel} cannot be added to the rota until allergen training is complete.`
  );
}

function findStaffRowById(staffIdInput) {
  const staffId = String(staffIdInput || "").trim();
  if (!staffId) return null;
  return (
    (staff.value || []).find((row) => {
      const possibleIds = [
        row?.id,
        row?.staffId,
        row?.staff_id,
        row?.uid,
        row?.userUid,
        row?.email,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      return possibleIds.includes(staffId) || normaliseStaffId(row) === staffId;
    }) || null
  );
}

function staffIdFromShiftData(shiftData) {
  return String(
    shiftData?.staffId ||
      shiftData?.staff_id ||
      shiftData?.employeeId ||
      shiftData?.employee_id ||
      ""
  ).trim();
}

/* ─── PDF generation ────────────────────────────────── */

const generatingRotaPdf = ref(false);
const generateRotaPdfCallable = httpsCallable(functions, "generateRotaPdf");

function downloadBase64Pdf({ pdfBase64, fileName }) {
  const byteCharacters = atob(pdfBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName || "rota.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

async function onGenerateRotaPdf() {
  if (!isOperations.value) {
    M.toast({
      html: "Only operations users can generate the rota PDF.",
    });
    return;
  }
  const v = String(venueId.value || "").trim();
  const w = String(weekId.value || "").trim();
  if (!v || !w) {
    M.toast({
      html: "Missing venue or week.",
    });
    return;
  }
  generatingRotaPdf.value = true;
  try {
    M.toast({
      html: "Generating rota PDF...",
    });
    const res = await generateRotaPdfCallable({
      venueId: v,
      weekId: w,
    });
    const data = res?.data || {};
    if (!data?.ok || !data?.pdfBase64) {
      throw new Error(data?.error || "Failed to generate rota PDF.");
    }
    downloadBase64Pdf({
      pdfBase64: data.pdfBase64,
      fileName: data.fileName || `rota-${w}.pdf`,
    });
    M.toast({
      html: "Rota PDF generated.",
    });
  } catch (e) {
    console.error("[RotaView] generate rota PDF failed:", e);
    M.toast({
      html: e?.message || "Failed to generate rota PDF.",
    });
  } finally {
    generatingRotaPdf.value = false;
  }
}

/* ─── Publish state ─────────────────────────────────── */

const published = ref(false);
const editEnabled = ref(true);
const publishing = ref(false);
const publishError = ref("");
let unsubscribeRotaDoc = null;

function stopRotaDocListener() {
  try {
    if (typeof unsubscribeRotaDoc === "function") {
      unsubscribeRotaDoc();
    }
  } finally {
    unsubscribeRotaDoc = null;
  }
}

function startRotaDocListener() {
  stopRotaDocListener();
  const v = String(venueId.value || "").trim();
  const w = String(weekId.value || "").trim();
  if (!v || !w) return;
  unsubscribeRotaDoc = onSnapshot(
    doc(db, `venues/${v}/rotas/${w}`),
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const status = String(data?.status || "").toLowerCase();
      published.value = data?.published === true || status === "published";
      if (published.value) {
        if (editEnabled.value !== true) {
          editEnabled.value = false;
        }
      } else {
        editEnabled.value = true;
      }
    },
    (err) => console.warn("[ROTA] listener error:", err)
  );
}

onBeforeUnmount(() => {
  stopRotaDocListener();
  if (typeof unsubscribeAuth === "function") {
    unsubscribeAuth();
  }
});

const canPublish = computed(
  () =>
    !!venueId.value &&
    !!weekId.value &&
    !loading.value &&
    !publishing.value &&
    shifts.value.length > 0
);

/* ─── Holiday blocks ────────────────────────────────── */

const holidayBlocks = ref([]);

async function loadHolidayBlocksForWeek() {
  const v = String(venueId.value || "").trim();
  if (!v || !Array.isArray(weekDays.value) || weekDays.value.length === 0) {
    holidayBlocks.value = [];
    return;
  }
  const startDate = String(weekDays.value[0]?.ymd || "").trim();
  const endDate = String(weekDays.value[weekDays.value.length - 1]?.ymd || "").trim();
  if (!startDate || !endDate) {
    holidayBlocks.value = [];
    return;
  }
  try {
    holidayBlocks.value = await fetchHolidayBlocksForDateRange({
      venueId: v,
      startDate,
      endDate,
    });
  } catch (e) {
    console.error("[RotaView] failed to load holiday blocks:", e);
    holidayBlocks.value = [];
  }
}

function holidayBlockForCell(staffId, ymd) {
  const sid = String(staffId || "").trim();
  const date = String(ymd || "").trim();
  if (!sid || !date) return null;
  return (
    (holidayBlocks.value || []).find((b) => {
      const blockStaffId = String(
        b.staffId || b.staff_id || b.employeeId || b.employee_id || ""
      ).trim();
      const blockDate = String(b.date || "").trim();
      const blockStatus = String(b.status || "")
        .trim()
        .toLowerCase();
      return blockStaffId === sid && blockDate === date && blockStatus === "approved";
    }) || null
  );
}

/* ─── Load ──────────────────────────────────────────── */

async function reload() {
  if (!venueId.value) return;
  await loadWeek(venueId.value);
  await loadHolidayBlocksForWeek();
  await loadAllergenTrainingGateForRota(venueId.value, staff.value);
  startRotaDocListener();
}

async function loadCurrentVenueWeek(v) {
  if (!v) return;
  await loadWeek(v);
  await loadHolidayBlocksForWeek();
  await loadAllergenTrainingGateForRota(v, staff.value);
  startRotaDocListener();
}

watch(
  venueId,
  async (v) => {
    stopRotaDocListener();
    if (!v) {
      holidayBlocks.value = [];
      allergenTrainingByStaffId.value = {};
      allergenTrainingLoaded.value = false;
      return;
    }
    published.value = false;
    editEnabled.value = true;
    publishing.value = false;
    publishError.value = "";
    await loadCurrentVenueWeek(v);
  },
  {
    immediate: true,
  }
);

watch(
  () => weekId.value,
  async (w) => {
    stopRotaDocListener();
    if (!venueId.value || !w) {
      return;
    }
    publishError.value = "";
    editEnabled.value = true;
    await loadWeek(venueId.value);
    await loadHolidayBlocksForWeek();
    await loadAllergenTrainingGateForRota(venueId.value, staff.value);
    startRotaDocListener();
  }
);

/* ─── Shift interactions ────────────────────────────── */

const shiftModal = ref(null);
const activeShift = ref(null);
const saving = ref(false);

function ensureCanEditOrToast() {
  if (!canAddOrEdit.value) {
    M.toast({
      html: "This week is locked.",
    });
    return false;
  }
  if (published.value && !editEnabled.value) {
    M.toast({
      html: "Rota is published. Click Enable edit to make changes.",
    });
    return false;
  }
  return true;
}

function onCellClick({ staffRow, ymd }) {
  const blocked = holidayBlockForCell(staffRow?.id, ymd);
  if (blocked) {
    const staffLabel =
      staffRow?.staff_name ||
      staffRow?.staffName ||
      staffRow?.name ||
      "This staff member";
    M.toast({
      html: `${staffLabel} is on ${blocked.label || "Holiday"} for this date.`,
    });
    return;
  }
  if (!ensureCanEditOrToast()) {
    return;
  }
  if (!canAddStaffToRota(staffRow)) {
    M.toast({
      html: allergenBlockMessage(staffRow),
    });
    return;
  }
  activeShift.value = makeDefaultShiftDraft({
    staffRow,
    ymd,
  });
  shiftModal.value?.open?.();
}

function onShiftClick(shift) {
  if (!ensureCanEditOrToast()) {
    return;
  }
  activeShift.value = {
    ...shift,
  };
  shiftModal.value?.open?.();
}

/* ─── Approved holiday removal ────────────────────── */

const removingHoliday = ref(false);
const removeApprovedHolidayDayCallable = httpsCallable(
  functions,
  "removeApprovedHolidayDay"
);

async function onHolidayClick({ holiday, staffRow, staffId, ymd }) {
  if (!venueId.value || !ensureCanEditOrToast() || removingHoliday.value) {
    return;
  }
  const selectedVenue = String(venueId.value || "").trim();
  const selectedWeek = String(weekId.value || "").trim();
  const blockId = String(holiday?.id || "").trim();
  const selectedStaffId = String(staffId || staffRow?.id || "").trim();
  const date = String(ymd || holiday?.date || "").trim();

  if (!blockId || !selectedStaffId || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    M.toast({ html: "Cannot identify this holiday record." });
    return;
  }

  const staffName = String(
    staffRow?.staff_name ||
    staffRow?.staffName ||
    staffRow?.name ||
    "this employee"
  );
  const displayDate = new Date(`${date}T12:00:00`).toLocaleDateString(
    "en-GB",
    { weekday: "long", day: "numeric", month: "long", year: "numeric" }
  );
  const leaveType = String(holiday?.label || "Holiday");
  const confirmed = window.confirm(
    `Remove ${leaveType} for ${staffName} on ${displayDate}?\n\n` +
    "Only this date will be removed. Other approved dates will remain."
  );

  if (!confirmed) return;

  removingHoliday.value = true;
  try {
    const response = await removeApprovedHolidayDayCallable({
      venueId: selectedVenue,
      blockId,
      staffId: selectedStaffId,
      date,
    });

    if (response?.data?.ok !== true) {
      throw new Error(response?.data?.error || "Holiday removal failed.");
    }

    if (
      String(venueId.value || "").trim() === selectedVenue &&
      String(weekId.value || "").trim() === selectedWeek
    ) {
      await loadHolidayBlocksForWeek();
    }
    M.toast({ html: "Holiday removed for the selected date." });
  } catch (e) {
    console.error("[RotaView] holiday removal failed:", e);
    M.toast({ html: e?.message || "Could not remove holiday." });
  } finally {
    removingHoliday.value = false;
  }
}

async function onSaveShift(payload) {
  if (!venueId.value || !ensureCanEditOrToast()) {
    return;
  }
  const payloadStaffId = String(payload?.staffId || payload?.staff_id || "").trim();
  const staffRow = findStaffRowById(payloadStaffId);
  if (staffRow && !canAddStaffToRota(staffRow)) {
    M.toast({
      html: allergenBlockMessage(staffRow),
    });
    return;
  }
  if (!staffRow && payloadStaffId) {
    M.toast({
      html:
        "Cannot save this shift because the staff member could not be matched to a training record.",
    });
    return;
  }
  const blocked = holidayBlockForCell(
    payload?.staffId || payload?.staff_id,
    payload?.date
  );
  if (blocked) {
    M.toast({
      html: "Cannot save a shift on an approved holiday date.",
    });
    return;
  }
  saving.value = true;
  try {
    await createOrUpdateShift(venueId.value, payload);
    shiftModal.value?.close?.();
    M.toast({
      html: "Shift saved.",
    });
  } catch (e) {
    M.toast({
      html: e?.message || String(e),
    });
  } finally {
    saving.value = false;
  }
}

async function onDeleteShift(shiftId) {
  if (!venueId.value || !ensureCanEditOrToast()) {
    return;
  }
  saving.value = true;
  try {
    await deleteShift(venueId.value, shiftId);
    M.toast({
      html: "Shift deleted.",
    });
  } catch (e) {
    M.toast({
      html: e?.message || String(e),
    });
  } finally {
    saving.value = false;
  }
}

/* ─── Copy previous week rota ───────────────────────── */

const copyPreviousWeekBusy = ref(false);
const currentWeekHasExistingRota = computed(() => {
  return Number(shifts.value?.length || 0) > 0;
});
const canCopyPreviousWeek = computed(() => {
  return (
    !!venueId.value &&
    !!weekId.value &&
    Array.isArray(weekDays.value) &&
    weekDays.value.length === 7 &&
    !loading.value &&
    !publishing.value &&
    !copyPreviousWeekBusy.value &&
    !currentWeekHasExistingRota.value &&
    canAddOrEdit.value &&
    (!published.value || editEnabled.value)
  );
});

function parseISODateLocal(iso) {
  const value = String(iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const [year, month, day] = value.split("-").map((part) => Number(part));
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function toISODateLocal(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function addDaysISO(iso, days) {
  const date = parseISODateLocal(iso);
  if (!date) return "";
  date.setDate(date.getDate() + Number(days || 0));
  return toISODateLocal(date);
}

function previousWeekIdFromCurrentWeekId(currentWeekId) {
  return addDaysISO(currentWeekId, -7);
}

function dayIndexFromISO(iso) {
  const value = String(iso || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  return (new Date(`${value}T00:00:00`).getDay() + 6) % 7;
}

function cleanCopiedShiftData(sourceShift, targetDate) {
  const {
    id,
    createdAt,
    created_at,
    updatedAt,
    updated_at,
    publishedAt,
    published_at,
    emailedAt,
    emailed_at,
    ...rest
  } = sourceShift || {};
  return {
    ...rest,
    date: targetDate,
    dayIndex: dayIndexFromISO(targetDate),
    copiedFromPreviousWeek: true,
    copiedFromShiftId: id || "",
    copiedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

function shiftPassesAllergenGateForCopy(shiftData) {
  const sid = staffIdFromShiftData(shiftData);
  if (!sid) {
    return {
      allowed: false,
      reason: "Shift has no staff ID.",
    };
  }
  const staffRow = findStaffRowById(sid);
  if (!staffRow) {
    return {
      allowed: false,
      reason: `Staff member ${sid} is not in the current staff list.`,
    };
  }
  const allowed = canAddStaffToRota(staffRow);
  return {
    allowed,
    reason: allowed ? "" : allergenBlockMessage(staffRow),
  };
}

async function onCopyPreviousWeekRota() {
  if (currentWeekHasExistingRota.value) {
    M.toast({
      html: "This week already has shifts. Copy previous week is disabled.",
    });
    return;
  }
  if (!canCopyPreviousWeek.value) {
    M.toast({
      html: "Cannot copy the previous week into this rota.",
    });
    return;
  }
  if (!ensureCanEditOrToast()) {
    return;
  }
  const v = String(venueId.value || "").trim();
  const currentWeek = String(weekId.value || "").trim();
  const previousWeek = previousWeekIdFromCurrentWeekId(currentWeek);
  if (!v || !currentWeek || !previousWeek) {
    M.toast({
      html: "Missing venue or week.",
    });
    return;
  }
  if (!allergenTrainingLoaded.value) {
    await loadAllergenTrainingGateForRota(v, staff.value);
  }
  const confirmed = window.confirm("Copy last week's rota into the current rota?");
  if (!confirmed) return;
  copyPreviousWeekBusy.value = true;
  try {
    const previousShiftsSnap = await getDocs(
      collection(db, `venues/${v}/rotas/${previousWeek}/shifts`)
    );
    if (previousShiftsSnap.empty) {
      M.toast({
        html: "No shifts found in the previous week.",
      });
      return;
    }
    const currentWeekDates = new Set(
      (weekDays.value || []).map((day) => String(day?.ymd || "").trim()).filter(Boolean)
    );
    const blockedCopyReasons = [];
    const copiedShifts = previousShiftsSnap.docs
      .map((shiftDoc) => {
        const previousShift = {
          id: shiftDoc.id,
          ...(shiftDoc.data() || {}),
        };
        const previousDate = String(previousShift.date || "").trim();
        const targetDate = addDaysISO(previousDate, 7);
        if (!targetDate || !currentWeekDates.has(targetDate)) {
          return null;
        }
        const gateResult = shiftPassesAllergenGateForCopy(previousShift);
        if (!gateResult.allowed) {
          blockedCopyReasons.push(gateResult.reason);
          return null;
        }
        return cleanCopiedShiftData(previousShift, targetDate);
      })
      .filter(Boolean);

    if (copiedShifts.length === 0) {
      const uniqueReasons = [...new Set(blockedCopyReasons)].filter(Boolean);
      M.toast({
        html:
          uniqueReasons[0] ||
          "No shifts could be copied because staff training restrictions blocked them.",
      });
      return;
    }
    for (let i = 0; i < copiedShifts.length; i += 450) {
      const batch = writeBatch(db);
      copiedShifts.slice(i, i + 450).forEach((shiftData) => {
        const newShiftRef = doc(
          collection(db, `venues/${v}/rotas/${currentWeek}/shifts`)
        );
        batch.set(newShiftRef, shiftData);
      });
      batch.set(
        doc(db, `venues/${v}/rotas/${currentWeek}`),
        {
          updatedAt: serverTimestamp(),
          lastCopiedFromWeekId: previousWeek,
          lastCopiedAt: serverTimestamp(),
        },
        {
          merge: true,
        }
      );
      await batch.commit();
    }
    await loadWeek(v);
    await loadHolidayBlocksForWeek();
    await loadAllergenTrainingGateForRota(v, staff.value);
    startRotaDocListener();
    const blockedCount = blockedCopyReasons.length;
    M.toast({
      html:
        blockedCount > 0
          ? `Copied ${copiedShifts.length} shift(s). ${blockedCount} shift(s) were blocked due to allergen training restrictions.`
          : `Copied ${copiedShifts.length} shift(s) from previous week.`,
    });
  } catch (e) {
    console.error("[RotaView] copy previous week rota failed:", e);
    M.toast({
      html: e?.message || "Failed to copy previous week's rota.",
    });
  } finally {
    copyPreviousWeekBusy.value = false;
  }
}

/* ─── Snapshot population on publish ────────────────── */

function s(v) {
  return String(v ?? "").trim();
}

function isISODate(v) {
  return /^\d{4}-\d{2}-\d{2}$/.test(s(v));
}

async function populateShiftSnapshotsAfterPublish({ venueId: v, weekId: w }) {
  const staffList =
    Array.isArray(staff.value) && staff.value.length
      ? staff.value
      : await getDocs(
          query(collection(db, `venues/${v}/staff_details`), orderBy("staff_name", "asc"))
        ).then((snap) =>
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
  const staffIdx = new Map(staffList.map((r) => [s(r?.id), r]).filter(([id]) => id));
  const docs = (await getDocs(collection(db, `venues/${v}/rotas/${w}/shifts`))).docs;
  for (let i = 0; i < docs.length; i += 450) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + 450)) {
      const sh = d.data() || {};
      const staffRow = staffIdx.get(s(sh.staffId || sh.staff_id || ""));
      batch.set(
        d.ref,
        {
          updatedAt: serverTimestamp(),
          dayIndex: dayIndexFromISO(s(sh.date || "")),
          staffNameSnapshot: s(staffRow?.staff_name || ""),
          jobTitleSnapshot: s(staffRow?.job_title || staffRow?.jobTitle || ""),
          payRateSnapshot: Number(staffRow?.pay_rate ?? staffRow?.payRate ?? 0) || 0,
          role: s(sh.role || "") || s(staffRow?.job_title || staffRow?.jobTitle || ""),
        },
        {
          merge: true,
        }
      );
    }
    await batch.commit();
  }
  return {
    total: docs.length,
  };
}

/* ─── Publish ───────────────────────────────────────── */

const publishRotaCallable = httpsCallable(functions, "publishRota");

async function onPublish({ republish }) {
  const v = String(venueId.value || "").trim();
  const w = String(weekId.value || "").trim();
  if (!v || !w) return;

  if (!shifts.value?.length) {
    M.toast({
      html: "Add at least one shift before publishing.",
    });
    return;
  }

  publishing.value = true;
  publishError.value = "";

  try {
    const res = await publishRotaCallable({
      venueId: v,
      weekId: w,
      republish: !!republish,
    });
    const data = res?.data || {};

    if (!data?.ok) {
      throw new Error(data?.error || "Publish failed");
    }

    try {
      await populateShiftSnapshotsAfterPublish({
        venueId: v,
        weekId: w,
      });
    } catch (e) {
      M.toast({
        html: `Published, but snapshot fill failed: ${e?.message || e}`,
      });
    }

    editEnabled.value = false;
    M.toast({
      html: republish
        ? `Rota republished (${data.emailed || 0} emailed).`
        : `Rota published (${data.emailed || 0} emailed).`,
    });
  } catch (e) {
    publishError.value = e?.message || String(e);
  } finally {
    publishing.value = false;
  }
}

function onEnableEdit() {
  if (!published.value) return;
  editEnabled.value = true;
  M.toast({
    html: "Editing enabled. Make changes then republish.",
  });
}
</script>
